/**
 * SectionSuggestionsPanel Component
 *
 * A collapsible panel for showing AI-generated suggestions for canvas sections.
 * Integrates with the multi-panel Canvas layout (Canvas | Property/Suggestions | AI Coach).
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Sparkles, Loader2, AlertCircle, RefreshCw, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { canvasAIApi } from '@/lib/api/client'
import type { CanvasSection } from './canvasTemplates'

// Suggestion from AI
interface AISuggestion {
  id: string
  label: string
  description?: string
  entityType: string
  confidence: 'high' | 'medium' | 'low'
  reasoning?: string
}

interface SectionSuggestionsPanelProps {
  section: CanvasSection
  canvasInstanceUri: string
  accountId: string
  projectId: string
  apiKey: string
  canvasContext?: Record<string, string[]>
  onClose: () => void
  onAddSuggestion: (suggestion: {
    type: string
    label: string
    description?: string
    sectionId: string
    confidence: number
  }) => void
  className?: string
}

export function SectionSuggestionsPanel({
  section,
  canvasInstanceUri,
  accountId,
  projectId,
  apiKey,
  canvasContext,
  onClose,
  onAddSuggestion,
  className,
}: SectionSuggestionsPanelProps) {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())

  // Fetch suggestions when panel opens or section changes
  const fetchSuggestions = useCallback(async () => {
    if (!apiKey) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await canvasAIApi.generateSuggestions(
        accountId,
        projectId,
        canvasInstanceUri,
        {
          sectionKey: section.id,
          apiKey,
          canvasContext,
          limit: 8,
        }
      )
      setSuggestions(result.suggestions)
    } catch (err) {
      console.error('Failed to fetch suggestions:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [apiKey, accountId, projectId, canvasInstanceUri, section.id, canvasContext])

  // Only fetch when section changes, not when canvasContext updates
  useEffect(() => {
    fetchSuggestions()
    setAddedIds(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id])

  // Handle adding a suggestion
  const handleAddSuggestion = (suggestion: AISuggestion) => {
    const confidenceMap: Record<string, number> = {
      high: 0.9,
      medium: 0.7,
      low: 0.5,
    }

    onAddSuggestion({
      type: suggestion.entityType,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: section.id,
      confidence: confidenceMap[suggestion.confidence] || 0.7,
    })

    setAddedIds((prev) => new Set([...prev, suggestion.id]))
  }

  // Confidence badge colors
  const confidenceColors: Record<string, { bg: string; text: string; border: string }> = {
    high: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
    low: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  }

  return (
    <div className={cn('flex flex-col bg-white h-full', className)}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-warm-200 bg-gradient-to-r from-warm-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${section.color}20` }}
            >
              <Sparkles className="w-4 h-4" style={{ color: section.color }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-warm-800">AI Suggestions</h2>
              <p className="text-xs text-text-muted">{section.title}</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {!isLoading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSuggestions}
                className="h-7 w-7 p-0"
                title="Refresh suggestions"
              >
                <RefreshCw className="h-3.5 w-3.5 text-text-muted" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-7 w-7 p-0"
            >
              <X className="h-3.5 w-3.5 text-text-muted" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {isLoading ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ backgroundColor: `${section.color}10` }}
            >
              <Loader2
                className="w-6 h-6 animate-spin"
                style={{ color: section.color }}
              />
            </div>
            <h3 className="text-sm font-semibold text-warm-700 mb-1">
              Generating suggestions...
            </h3>
            <p className="text-xs text-text-muted leading-relaxed">
              Analyzing your canvas context
            </p>
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-sm font-semibold text-warm-700 mb-1">
              Failed to generate suggestions
            </h3>
            <p className="text-xs text-text-muted leading-relaxed mb-3">
              {error}
            </p>
            <Button
              size="sm"
              onClick={fetchSuggestions}
              className="h-8 px-3 text-xs"
              style={{
                backgroundColor: section.color,
                color: 'white',
              }}
            >
              Try again
            </Button>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ backgroundColor: `${section.color}10` }}
            >
              <Sparkles className="w-6 h-6" style={{ color: section.color }} />
            </div>
            <h3 className="text-sm font-semibold text-warm-700 mb-1">
              No suggestions available
            </h3>
            <p className="text-xs text-text-muted leading-relaxed">
              Try adding more content to other sections first, then refresh.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.map((suggestion) => {
              const isAdded = addedIds.has(suggestion.id)
              const colors = confidenceColors[suggestion.confidence] || confidenceColors.medium

              return (
                <div
                  key={suggestion.id}
                  className={cn(
                    'rounded-lg border p-3 transition-all',
                    isAdded
                      ? 'bg-warm-50 border-warm-200 opacity-60'
                      : 'bg-white border-warm-200 hover:border-warm-300 hover:shadow-sm'
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2 flex-wrap">
                        <span
                          className={cn(
                            'text-sm font-medium',
                            isAdded ? 'text-text-muted line-through' : 'text-warm-800'
                          )}
                        >
                          {suggestion.label}
                        </span>
                        <span
                          className={cn(
                            'text-[10px] font-medium px-2 py-0.5 rounded capitalize flex-shrink-0',
                            colors.bg,
                            colors.text,
                            colors.border,
                            'border'
                          )}
                        >
                          {suggestion.confidence}
                        </span>
                      </div>
                      {suggestion.description && (
                        <p className="text-xs text-text-muted leading-relaxed">
                          {suggestion.description}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddSuggestion(suggestion)}
                      disabled={isAdded}
                      className={cn(
                        'h-8 w-8 p-0 flex-shrink-0',
                        isAdded && 'opacity-50 cursor-not-allowed'
                      )}
                      style={{
                        backgroundColor: isAdded ? '#f3f4f6' : section.color,
                        color: isAdded ? '#9ca3af' : 'white',
                      }}
                      title={isAdded ? 'Added' : 'Add to canvas'}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer - Context indicator */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-warm-200 bg-warm-50/50">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Sparkles className="h-3 w-3 flex-shrink-0" style={{ color: section.color }} />
          <span>
            Suggestions based on {Object.keys(canvasContext || {}).length} sections with content
          </span>
        </div>
      </div>
    </div>
  )
}
